import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Composer } from '@/modules/debug/linkedin/components/composer/Composer';
import { MessageList } from '@/modules/debug/linkedin/components/messageList/MessageList';
import { ThreadHeader } from '@/modules/debug/linkedin/components/threadHeader/ThreadHeader';
import { ThreadList } from '@/modules/debug/linkedin/components/threadList/ThreadList';
import { formatTime } from '@/modules/debug/linkedin/format';
import {
  autoReplyNote,
  lastAuthor,
} from '@/modules/debug/linkedin/linkedInDebug';
import { useDebugLinkedInAccountsQuery } from '@/modules/debug/linkedin/queries/useDebugLinkedInAccountsQuery';
import { useDebugLinkedInMessagesQuery } from '@/modules/debug/linkedin/queries/useDebugLinkedInMessagesQuery';
import { useDebugLinkedInSendAsCandidateMutation } from '@/modules/debug/linkedin/queries/useDebugLinkedInSendAsCandidateMutation';
import { useDebugLinkedInSendAsRecruiterMutation } from '@/modules/debug/linkedin/queries/useDebugLinkedInSendAsRecruiterMutation';
import { useDebugLinkedInSendNowMutation } from '@/modules/debug/linkedin/queries/useDebugLinkedInSendNowMutation';
import { useDebugLinkedInThreadsQuery } from '@/modules/debug/linkedin/queries/useDebugLinkedInThreadsQuery';
import type {
  DebugLinkedInAccount,
  DebugLinkedInMessageAuthor,
} from '@/modules/debug/linkedin/types';
import { Badge } from '@/modules/ui/components/badge/Badge';
import Loader from '@/modules/ui/components/loader/Loader';
import { PageHeader } from '@/modules/ui/components/page-header/PageHeader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/modules/ui/components/select/Select';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

interface AccountSelectorProps {
  accounts: DebugLinkedInAccount[];
  value: string | null;
  onChange: (id: string) => void;
}

function AccountSelector({ accounts, value, onChange }: AccountSelectorProps) {
  return (
    <Select value={value ?? undefined} onValueChange={onChange}>
      <SelectTrigger className="w-[320px]">
        <SelectValue placeholder="Select a recruiter account" />
      </SelectTrigger>
      <SelectContent>
        {accounts.map((account) => (
          <SelectItem
            key={account.userExternalAccountId}
            value={account.userExternalAccountId}
          >
            <div className="flex flex-col">
              <span className="text-sm font-medium">{account.userName}</span>
              <span className="text-muted-foreground text-xs">
                {account.userEmail ?? account.accountId ?? '—'}
              </span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function LinkedInDebug() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [role, setRole] = useState<DebugLinkedInMessageAuthor>('Candidate');

  const accountsQuery = useDebugLinkedInAccountsQuery();
  const accounts = accountsQuery.data ?? [];

  const accountParam = searchParams.get('account');
  const selectedAccountId = accounts.some(
    (a) => a.userExternalAccountId === accountParam,
  )
    ? accountParam
    : (accounts[0]?.userExternalAccountId ?? null);
  const activeAccount =
    accounts.find((a) => a.userExternalAccountId === selectedAccountId) ?? null;

  const threadsQuery = useDebugLinkedInThreadsQuery(selectedAccountId);
  const threads = threadsQuery.data ?? [];

  const threadParam = searchParams.get('thread');
  const selectedThreadId = threads.some((t) => t.threadId === threadParam)
    ? threadParam
    : (threads[0]?.threadId ?? null);
  const activeThread =
    threads.find((t) => t.threadId === selectedThreadId) ?? null;

  const messagesQuery = useDebugLinkedInMessagesQuery(selectedThreadId);
  const messages = messagesQuery.data ?? [];

  const sendAsCandidate = useDebugLinkedInSendAsCandidateMutation();
  const sendAsRecruiter = useDebugLinkedInSendAsRecruiterMutation();
  const sendNow = useDebugLinkedInSendNowMutation();

  const select = (account: string | null, thread: string | null) => {
    const next = new URLSearchParams();
    if (account) next.set('account', account);
    if (thread) next.set('thread', thread);
    setSearchParams(next, { replace: true });
    sendAsCandidate.reset();
    sendAsRecruiter.reset();
  };

  const composerMutation =
    role === 'Candidate' ? sendAsCandidate : sendAsRecruiter;

  const handleSend = (text: string) => {
    if (!selectedThreadId) return;
    composerMutation.mutate({ threadId: selectedThreadId, message: text });
  };

  const handleSendNow = (messageId: string) => {
    const previousSchedule = messages.find(
      (m) => m.id === messageId,
    )?.scheduledAt;
    sendNow.mutate(
      { messageId },
      {
        onSuccess: (message) => {
          if (!message || message.status === 'Sent') return;
          // SendMessage moves the schedule only when the sender is rate limited;
          // other skips leave the message as it was or turn it back into a draft.
          const rescheduled =
            message.status === 'Pending' &&
            message.scheduledAt !== previousSchedule;
          toast.warning(
            rescheduled
              ? `Not sent: rate limited, rescheduled for ${formatTime(message.scheduledAt)}`
              : `Not sent: the send rules skipped it, the message is ${message.status.toLowerCase()}`,
          );
        },
        onError: (error) =>
          toast.error(actionErrorMessage('send the message now', error)),
      },
    );
  };

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col gap-4 px-6 py-4">
      <PageHeader
        title="Messaging simulator"
        subtitle="Test responses with real candidate data"
      />

      <div className="flex flex-wrap items-center gap-4">
        {accountsQuery.isLoading ? (
          <Loader />
        ) : accounts.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No connected messaging accounts found. Connect an account first.
          </p>
        ) : (
          <AccountSelector
            accounts={accounts}
            value={selectedAccountId}
            onChange={(id) => select(id, null)}
          />
        )}
        {activeAccount && (
          <Badge variant="outline" className="font-mono text-xs">
            account_id: {activeAccount.accountId ?? '—'}
          </Badge>
        )}
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-[360px_1fr] grid-rows-1 overflow-hidden rounded-lg border bg-white">
        <aside className="overflow-y-auto border-r">
          <ThreadList
            key={selectedAccountId}
            threads={threads}
            selectedThreadId={selectedThreadId}
            onSelect={(id) => select(selectedAccountId, id)}
            loading={threadsQuery.isLoading}
          />
        </aside>

        <section className="flex min-h-0 flex-col">
          {activeThread ? (
            <>
              <ThreadHeader thread={activeThread} />

              <MessageList
                messages={messages}
                candidateJobs={activeThread.candidateJobs}
                loading={messagesQuery.isLoading}
                autoReplyNote={autoReplyNote(
                  activeThread,
                  lastAuthor(messages),
                  new Date(),
                )}
                onSendNow={handleSendNow}
                sendingNowId={
                  sendNow.isPending ? sendNow.variables.messageId : null
                }
              />

              <Composer
                key={activeThread.threadId}
                role={role}
                onRoleChange={setRole}
                onSend={handleSend}
                sending={sendAsCandidate.isPending || sendAsRecruiter.isPending}
                error={
                  composerMutation.error
                    ? actionErrorMessage(
                        'send the message',
                        composerMutation.error,
                      )
                    : null
                }
                canReplyAsCandidate={messages.some(
                  (m) => m.author === 'Recruiter' && m.status === 'Sent',
                )}
              />
            </>
          ) : (
            <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
              Select a thread to start chatting.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
